import React, { useEffect, useRef, useState } from 'react';
import { Section, WrapperExternal } from '@/components/atoms';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import styled from 'styled-components';
import createComponents from '@/utils/createComponents';
import useAbTest from '@/hooks/useAbTest';
import useBreakpoint from '@/hooks/useBreakpoint';
import { throttle } from '@/utils/index';
import { CollectionHandler, CollectionHeader } from '@/components/organisms';
import { CollectionFooter, LoadingOverlay } from '@/components/molecules';
import ResizeObserver from 'resize-observer-polyfill';
import { setStickyPositionTop } from '@/utils/Catalog';
import { ICollectionPageTemplateCmp } from './ICollectionPageTemplateCmp';
import CollectionInfo from '@/utils/CollectionInfo';

const HeaderBreadCrumbs = styled.div`
  display: flex;
  flex-direction: column-reverse;
  ${props => props.theme.breakpoints.up('md')} {
    flex-direction: column;
  }
  margin-bottom: 8px;
`;

const ScrollToElement = styled.div`
  padding-top: 40px; // for anchor tag to shop products
  margin-top: -40px; // for anchor tag to shop products
`;

const CollectionPageTemplate: React.FC<ICollectionPageTemplateCmp> = ({
  content,
  cmsPage,
  cmsCollectionTags,
  locale,
  collectionPagePatterns,
  catalogProducts,
  catalogFilters,
  catalogCategories,
  serverSideCatalogQuery,
}) => {
  const scrollToRef = useRef<HTMLDivElement>(null);
  const appProps = useAbTest(content);
  const breakpoints = useBreakpoint();

  // state
  const [pageInfo, setPageInfo] = useState(cmsPage);

  // from redux
  const { productsInfo, collectionInfo } = useSelector(
    (state: RootState) => state.collectionInfoReducer
  );

  useEffect(() => {
    const filtersContainer = document.getElementById('filtersContainer');
    if (filtersContainer) {
      const isFilterContainerVisible =
        window.getComputedStyle(filtersContainer, null).display !== 'none';
      if (isFilterContainerVisible) {
        const filtersPanel = document.getElementById('filtersPanel');
        const stickyFilters = document.getElementById('stickyFilters');

        if (stickyFilters) {
          setStickyPositionTop(filtersContainer, stickyFilters);
        }

        let lastPanelHeight: null | {} = null;

        const resizeObserver = new ResizeObserver(entries => {
          for (let entry of entries) {
            if (
              entry.contentRect &&
              entry.contentRect.height &&
              entry.contentRect.height !== lastPanelHeight
            ) {
              lastPanelHeight = entry.contentRect;
              const stickyFilters = document.getElementById('stickyFilters');
              // const filtersContainer =
              // 	document.getElementById('filtersContainer');
              const isFilterContainerVisible =
                window.getComputedStyle(filtersContainer, null).display ===
                'flex';

              if (isFilterContainerVisible && stickyFilters) {
                setStickyPositionTop(filtersContainer, stickyFilters);
              }
            }
          }
        });

        let lastScroll = 0;

        const onScroll = throttle(() => {
          const stickyFilters = document.getElementById('stickyFilters');
          // const filtersContainer =
          // 	document.getElementById('filtersContainer');
          const isFilterContainerVisible =
            window.getComputedStyle(filtersContainer, null).display === 'flex';

          if (
            !stickyFilters ||
            !isFilterContainerVisible ||
            (breakpoints !== 'md' &&
              breakpoints !== 'lg' &&
              breakpoints !== 'xl')
          ) {
            return false;
          }
          const main = document.getElementsByTagName('main');

          const clientHeight = stickyFilters.clientHeight;
          const filtersContOffsetTop = filtersContainer?.offsetTop;
          const filterWrapperOffsetTop = stickyFilters?.offsetTop;
          const headerOffset = main[0].offsetTop;
          const scrollY = window.scrollY;
          const scrollDirection = lastScroll - scrollY > 0 ? 'up' : 'down';
          lastScroll = scrollY;

          const stickyElems = document.querySelectorAll('[data-issticky]');

          let stickyOffset = 0;
          stickyElems.forEach(el => {
            stickyOffset += el.clientHeight;
          });

          if (clientHeight > window.innerHeight - headerOffset) {
            if (scrollY <= filtersContOffsetTop) {
              stickyFilters.style.position = 'relative';
              stickyFilters.style.top = '0px';
            }
            if (
              scrollDirection === 'up' &&
              scrollY > filtersContOffsetTop &&
              stickyFilters.style.position !== 'relative' &&
              scrollY > filtersContOffsetTop - headerOffset + 70
            ) {
              stickyFilters.style.position = 'relative';
              stickyFilters.style.top = filterWrapperOffsetTop + 'px';
            }
            if (
              scrollDirection === 'up' &&
              scrollY > filtersContOffsetTop &&
              scrollY - filtersContOffsetTop - headerOffset + 70 <
                filterWrapperOffsetTop &&
              stickyFilters.style.position === 'relative'
            ) {
              stickyFilters.style.position = 'sticky';
              stickyFilters.style.top = stickyOffset + 'px';
            }

            if (
              scrollDirection === 'down' &&
              scrollY > filtersContOffsetTop &&
              stickyFilters?.style.top === stickyOffset + 'px' &&
              stickyFilters.style.position !== 'relative'
            ) {
              stickyFilters.style.position = 'relative';
              stickyFilters.style.top = filterWrapperOffsetTop + 'px';
            }

            if (
              stickyFilters &&
              filtersContOffsetTop &&
              clientHeight &&
              stickyFilters.style.position === 'relative' &&
              scrollDirection === 'down' &&
              scrollY >
                filterWrapperOffsetTop +
                  filtersContOffsetTop +
                  clientHeight -
                  window.innerHeight +
                  headerOffset -
                  15
            ) {
              stickyFilters.style.position = 'sticky';
              stickyFilters.style.top =
                window.innerHeight - clientHeight + 50 + 'px';
            }
          } else {
            stickyFilters.style.position = 'sticky';
            stickyFilters.style.top = stickyOffset + headerOffset + 'px';
          }
        }, 10);

        if (
          (breakpoints === 'md' ||
            breakpoints === 'lg' ||
            breakpoints === 'xl') &&
          filtersPanel
        ) {
          resizeObserver.observe(filtersPanel as Element);
          window.addEventListener('scroll', onScroll);
        }

        return () => {
          window.removeEventListener('scroll', onScroll);
          if (filtersPanel) {
            resizeObserver.unobserve(filtersPanel as Element);
          }
        };
      }
    }
  }, [breakpoints]);

  useEffect(() => {
    if (collectionInfo && Object.keys(collectionInfo).length > 0) {
      setPageInfo(collectionInfo);
    }
  }, [collectionInfo]);

  let cont = null;
  if (appProps) {
    cont = appProps.map((block: any, index: number) => {
      let marginBottom: string | undefined = block.fields.marginBottom;

      if (block.sys.contentType.sys.id === 'section') {
        // if (block.lastOne) {
        //   marginBottom = 'none';
        // }
        // if (block.mode !== 'content') {
        //   noHeight = true;
        //   marginBottom = 'none';
        // }

        let rest = {};
        if (block.fields.organism?.sys?.contentType?.sys?.id === 'popupTimer') {
          rest = {
            index: index,
            bgcolor: block.fields.backgroundColor || null,
            visibility: block.fields.visibility || null,
          };
        } else {
          rest = {
            index: index,
            bgcolor: block.fields.backgroundColor || null,
          };
        }

        return block ? (
          <Section
            key={index + (block.fields.organism?.sys.contentType.sys.id || '')}
            marginBottom={marginBottom}
            heading={block.fields.heading}
            visibility={block.fields?.visibility}>
            <WrapperExternal>
              {createComponents(block.fields.organism, rest)}
            </WrapperExternal>
          </Section>
        ) : null;
      }
    });
  }

  return (
    <>
      <ScrollToElement ref={scrollToRef}></ScrollToElement>
      <CollectionInfo
        cmsCollectionTags={cmsCollectionTags}
        cmsPagePatterns={collectionPagePatterns}
      />
      <HeaderBreadCrumbs>
        <CollectionHeader
          cmsCollectionTags={cmsCollectionTags}
          title={pageInfo.title}
          descriptionRich={pageInfo.descriptionRich}
          numProducts={productsInfo?.numProducts}
        />
      </HeaderBreadCrumbs>
      <WrapperExternal>
        <LoadingOverlay></LoadingOverlay>
        <CollectionHandler
          serverCategories={catalogCategories}
          serverFilters={catalogFilters}
          serverProducts={catalogProducts}
          cmsCollectionTags={cmsCollectionTags}
          serverSideCatalogQuery={serverSideCatalogQuery}></CollectionHandler>
        <CollectionFooter
          bottomDescriptionRich={pageInfo?.bottomDescriptionRich}
          locale={locale}
        />

        {cont ? cont : null}
      </WrapperExternal>
    </>
  );
};

export default CollectionPageTemplate;
